import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Input, Select, Label, Button, Icon } from '../../components/ui';

const JOURNALS = ['', 'VE', 'AC', 'BQ', 'CA', 'OD'];
const STATUSES = ['', 'POSTED', 'DRAFT', 'REVERSED'];

function toPeriodKey(d = new Date()) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export default function AccountingJournal() {
  const [periodKey, setPeriodKey] = useState(toPeriodKey());
  const [journalCode, setJournalCode] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const queryParams = useMemo(() => {
    const p = {};
    if (periodKey) p.periodKey = periodKey;
    if (journalCode) p.journalCode = journalCode;
    if (status) p.status = status;
    if (q) p.q = q;
    p.limit = 50;
    p.skip = 0;
    return p;
  }, [periodKey, journalCode, status, q]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/accounting/entries', { params: queryParams });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const loadDetail = async (entryId) => {
    try {
      setSelectedEntryId(entryId);
      setDetail(null);
      setDetailLoading(true);
      const res = await api.get(`/accounting/entries/${entryId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconBookOpen" className="text-indigo-600 mr-3" />
          Journal / Écritures comptables
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Affiche les écritures générées (ventes, achats, trésorerie).
        </p>
      </div>

      {/* Filtres */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Période (YYYY-MM)</Label>
            <Input
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
              placeholder="2026-02"
            />
          </div>

          <div>
            <Label className="text-xs text-gray-500">Journal</Label>
            <Select
              value={journalCode}
              onChange={(e) => setJournalCode(e.target.value)}
            >
              {JOURNALS.map((j) => (
                <option key={j} value={j}>
                  {j ? j : 'Tous'}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label className="text-xs text-gray-500">Statut</Label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s ? s : 'Tous'}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label className="text-xs text-gray-500">Recherche</Label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Libellé / Référence"
            />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-600">
                {loading ? 'Chargement…' : `${items.length} / ${total} écritures`}
              </div>
              <Button
                type="button"
                onClick={load}
                disabled={loading}
                variant="secondary"
                size="sm"
              >
                Rafraîchir
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Journal</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">N°</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Libellé</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Source</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                        Aucune écriture
                      </td>
                    </tr>
                  ) : (
                    items.map((e) => {
                      const active = selectedEntryId && String(e._id) === String(selectedEntryId);
                      return (
                        <tr key={e._id} className={active ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            {e.entryDate ? new Date(e.entryDate).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">{e.journalCode}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{e.entryNumber}</td>
                          <td className="px-3 py-2 text-sm text-gray-800">{e.label || '-'}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {e.sourceType} {e.sourceRef ? `— ${e.sourceRef}` : ''}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              onClick={() => loadDetail(e._id)}
                              size="sm"
                            >
                              Détails
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Détail */}
        <Card>
          <div className="font-semibold text-gray-800 mb-2">Détail</div>

          {detailLoading ? (
            <div className="text-gray-600">Chargement…</div>
          ) : !detail?.entry ? (
            <div className="text-gray-500 text-sm">Sélectionne une écriture.</div>
          ) : (
            <>
              <div className="text-sm text-gray-700">
                <div><span className="text-gray-500">Journal:</span> {detail.entry.journalCode}</div>
                <div><span className="text-gray-500">N°:</span> {detail.entry.entryNumber}</div>
                <div><span className="text-gray-500">Période:</span> {detail.entry.periodKey}</div>
                <div><span className="text-gray-500">Libellé:</span> {detail.entry.label || '-'}</div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Compte</th>
                      <th className="px-2 py-2 text-right text-xs text-gray-500 uppercase">Débit</th>
                      <th className="px-2 py-2 text-right text-xs text-gray-500 uppercase">Crédit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(detail.lines || []).map((l) => (
                      <tr key={l._id || l.lineNo}>
                        <td className="px-2 py-2 text-sm text-gray-800">
                          {l.accountCode} {l.accountLabel ? `— ${l.accountLabel}` : ''}
                        </td>
                        <td className="px-2 py-2 text-sm text-right text-gray-700">{Number(l.debit || 0).toFixed(3)}</td>
                        <td className="px-2 py-2 text-sm text-right text-gray-700">{Number(l.credit || 0).toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
